// ============================================
// pages/transportation/TransportBillsList.tsx
// Transport Bills (proforma 2 — aggregated trips → payable bill).
// 4-level approval workflow: draft → submitted → dm_verified → manager_approved
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Receipt, Loader2, Plus, Eye, Edit2, CheckCircle, XCircle, ShieldCheck, Send, Layers, Paperclip,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  seasonsAPI, districtsAPI, commoditiesAPI, transportBillsAPI,
} from '../../api/services';
import { UserRole, formatAmount, num } from '../../types/markfed';
import type { Season, District } from '../../types/markfed';

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; border: string }> = {
  draft:            { label: 'Draft',            bg: 'bg-yellow-50',  text: 'text-yellow-700', border: 'border-yellow-300' },
  submitted:        { label: 'Submitted',        bg: 'bg-blue-50',    text: 'text-blue-700',   border: 'border-blue-300' },
  dm_verified:      { label: 'DM Verified',      bg: 'bg-orange-50',  text: 'text-orange-700', border: 'border-orange-300' },
  manager_approved: { label: 'Manager Approved', bg: 'bg-green-50',   text: 'text-green-700',  border: 'border-green-300' },
  rejected:         { label: 'Rejected',         bg: 'bg-red-50',     text: 'text-red-700',    border: 'border-red-300' },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, bg: 'bg-gray-100', text: 'text-gray-500', border: 'border-gray-300' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
};

export const TransportBillsList: React.FC = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();

  const canSubmit = hasRole(UserRole.DM, UserRole.AO_CAO, UserRole.SUPER_ADMIN);
  const canVerify = hasRole(UserRole.AO_CAO, UserRole.SUPER_ADMIN);
  const canApprove = hasRole(UserRole.MD, UserRole.SUPER_ADMIN);

  const [seasons, setSeasons] = useState<Season[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [commodities, setCommodities] = useState<any[]>([]);
  const [bills, setBills] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
  const [commodityFilter, setCommodityFilter] = useState<number>(0);
  const [districtFilter, setDistrictFilter] = useState<number>(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [rejectModal, setRejectModal] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const districtMap = useMemo(() => {
    const m: Record<number, string> = {};
    districts.forEach((d) => { m[d.id] = d.name; });
    return m;
  }, [districts]);

  const commodityMap = useMemo(() => {
    const m: Record<number, string> = {};
    commodities.forEach((c) => { m[c.id] = c.name; });
    return m;
  }, [commodities]);

  useEffect(() => {
    (async () => {
      try {
        const [s, d, c] = await Promise.all([seasonsAPI.list(), districtsAPI.list(), commoditiesAPI.list()]);
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        setDistricts(d.data);
        setCommodities(c.data);
        const active = list.find((x) => x.is_active) || list[0];
        if (active) setSelectedSeasonId(active.id);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedSeasonId) return;
    (async () => {
      setLoading(true);
      try {
        const params: any = {};
        if (commodityFilter) params.commodity_id = commodityFilter;
        if (districtFilter) params.district_id = districtFilter;
        const res = await transportBillsAPI.list(selectedSeasonId, params);
        const raw = res.data;
        setBills(Array.isArray(raw) ? raw : (raw as any)?.data || []);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load transport bills');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedSeasonId, commodityFilter, districtFilter]);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return bills;
    return bills.filter((b) => (b.status || 'draft') === statusFilter);
  }, [bills, statusFilter]);

  const totals = useMemo(() => {
    return filtered.reduce(
      (acc, b) => ({
        count: acc.count + 1,
        claimed: acc.claimed + num(b.total_amount_claimed),
        paid: acc.paid + num(b.payment_released),
        balance: acc.balance + num(b.balance_to_release),
      }),
      { count: 0, claimed: 0, paid: 0, balance: 0 },
    );
  }, [filtered]);

  const advance = async (id: number, fn: () => Promise<any>, nextStatus: string, msg: string) => {
    setActionLoading(id);
    setMessage(null);
    try {
      await fn();
      setBills((prev) => prev.map((b) => (b.id === id ? { ...b, status: nextStatus } : b)));
      setMessage({ type: 'success', text: msg });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || `Failed: ${msg}` });
    } finally {
      setActionLoading(null);
    }
  };

  const handleAggregate = (id: number) =>
    window.confirm('Re-aggregate this bill from its trips? This recomputes totals.') &&
    advance(id, () => transportBillsAPI.aggregate(selectedSeasonId, id), 'draft', 'Aggregated from trips');

  const handleSubmit = (id: number) =>
    window.confirm('Submit for verification?') &&
    advance(id, () => transportBillsAPI.submit(selectedSeasonId, id), 'submitted', 'Submitted');

  const handleVerify = (id: number) =>
    window.confirm('Verify as DM/CAO?') &&
    advance(id, () => transportBillsAPI.verify(selectedSeasonId, id, {}), 'dm_verified', 'Verified');

  const handleApprove = (id: number) =>
    window.confirm('Approve as MD?') &&
    advance(id, () => transportBillsAPI.approve(selectedSeasonId, id, {}), 'manager_approved', 'Approved');

  const handleReject = async () => {
    if (!rejectModal || !rejectReason.trim()) return;
    setActionLoading(rejectModal);
    setMessage(null);
    try {
      await transportBillsAPI.reject(selectedSeasonId, rejectModal, rejectReason.trim());
      setBills((prev) =>
        prev.map((b) => (b.id === rejectModal ? { ...b, status: 'rejected', rejection_reason: rejectReason.trim() } : b)),
      );
      setMessage({ type: 'success', text: 'Rejected' });
      setRejectModal(null);
      setRejectReason('');
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to reject' });
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (s?: string) => {
    if (!s) return '--';
    try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return s; }
  };

  if (loading && bills.length === 0) {
    return (
      <div className="max-w-7xl mx-auto flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
        <span className="ml-2 text-gray-500">Loading...</span>
      </div>
    );
  }

  if (error && bills.length === 0) {
    return (
      <div className="max-w-7xl mx-auto py-10">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm flex items-center gap-2">
          <XCircle className="w-4 h-4" /> {error}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Receipt className="w-7 h-7 text-blue-600" />
            Transport Bills
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Aggregated transporter bills from PC → godown trips, with TDS &amp; payment tracking
          </p>
        </div>
        <button
          onClick={() => navigate('/transportation/billing/new')}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg text-sm"
        >
          <Plus className="w-4 h-4" />
          Add Transport Bill
        </button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Total Bills</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{totals.count}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Total Claimed</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">{formatAmount(totals.claimed)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Total Paid</p>
          <p className="text-2xl font-bold text-green-600 mt-1">{formatAmount(totals.paid)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Balance Outstanding</p>
          <p className="text-2xl font-bold text-orange-600 mt-1">{formatAmount(totals.balance)}</p>
        </div>
      </div>

      <div className="mb-4 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Season</label>
            <select value={selectedSeasonId} onChange={(e) => setSelectedSeasonId(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
              {seasons.map((s) => (<option key={s.id} value={s.id}>{s.season_name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Commodity</label>
            <select value={commodityFilter} onChange={(e) => setCommodityFilter(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
              <option value={0}>All Commodities</option>
              {commodities.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">District</label>
            <select value={districtFilter} onChange={(e) => setDistrictFilter(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
              <option value={0}>All Districts</option>
              {districts.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="dm_verified">DM Verified</option>
              <option value="manager_approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {message && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          <span>{message.text}</span>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Bill No</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Bill Date</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">District</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Commodity</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Contractor</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Trips</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Bags</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Wt (Qtl)</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Amt Claimed</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Amt Verified</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Paid</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">TDS</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Balance</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filtered.length === 0 ? (
                <tr><td colSpan={16} className="px-4 py-12 text-center text-gray-400">
                  No transport bills found. Click "Add Transport Bill" to create one.
                </td></tr>
              ) : (
                filtered.map((b, i) => {
                  const st = b.status || 'draft';
                  const isLoading = actionLoading === b.id;
                  return (
                    <tr key={b.id} className="hover:bg-gray-50">
                      <td className="px-3 py-3 text-xs text-gray-500">{i + 1}</td>
                      <td className="px-3 py-3 text-xs font-medium text-gray-900">
                        <span className="inline-flex items-center gap-1.5">
                          {b.bill_no || '--'}
                          {b.invoice_file_name && <Paperclip className="w-3 h-3 text-blue-600" aria-label="Invoice attached" />}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-600">{formatDate(b.bill_date)}</td>
                      <td className="px-3 py-3 text-xs text-gray-700">{b.district?.name || districtMap[b.district_id] || '--'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700">{b.commodity?.name || commodityMap[b.commodity_id] || '--'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700">{b.contractor?.firm_name || b.contractor?.name || '--'}</td>
                      <td className="px-3 py-3 text-xs text-right text-gray-700">{num(b.total_trips).toLocaleString('en-IN')}</td>
                      <td className="px-3 py-3 text-xs text-right text-gray-700">{num(b.total_bags).toLocaleString('en-IN')}</td>
                      <td className="px-3 py-3 text-xs text-right text-gray-700">{num(b.total_weight_qtl).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                      <td className="px-3 py-3 text-xs text-right text-gray-700">{formatAmount(num(b.total_amount_claimed))}</td>
                      <td className="px-3 py-3 text-xs text-right font-medium text-gray-900">{formatAmount(num(b.total_amount_verified))}</td>
                      <td className="px-3 py-3 text-xs text-right text-green-700">{formatAmount(num(b.payment_released))}</td>
                      <td className="px-3 py-3 text-xs text-right text-gray-700">{formatAmount(num(b.tds_amount))}</td>
                      <td className="px-3 py-3 text-xs text-right text-orange-700 font-medium">{formatAmount(num(b.balance_to_release))}</td>
                      <td className="px-3 py-3 text-center"><StatusBadge status={st} /></td>
                      <td className="px-3 py-3 text-center">
                        <div className="flex items-center justify-center gap-1.5 flex-wrap">
                          <button
                            onClick={() => navigate(`/transportation/billing/${b.id}`)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                          >
                            <Eye className="w-3 h-3" /> View
                          </button>
                          {(st === 'draft' || st === 'rejected') && (
                            <>
                              <button
                                onClick={() => navigate(`/transportation/billing/${b.id}`)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                              >
                                <Edit2 className="w-3 h-3" /> Edit
                              </button>
                              <button
                                onClick={() => handleAggregate(b.id)} disabled={isLoading}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50"
                                title="Re-aggregate totals from matching trips (by bill_no)"
                              >
                                <Layers className="w-3 h-3" /> Aggregate
                              </button>
                            </>
                          )}
                          {st === 'draft' && canSubmit && (
                            <button
                              onClick={() => handleSubmit(b.id)} disabled={isLoading}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
                            >
                              {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                              Submit
                            </button>
                          )}
                          {st === 'submitted' && canVerify && (
                            <button
                              onClick={() => handleVerify(b.id)} disabled={isLoading}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg disabled:opacity-50"
                            >
                              <ShieldCheck className="w-3 h-3" /> Verify
                            </button>
                          )}
                          {st === 'dm_verified' && canApprove && (
                            <button
                              onClick={() => handleApprove(b.id)} disabled={isLoading}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50"
                            >
                              <CheckCircle className="w-3 h-3" /> Approve
                            </button>
                          )}
                          {['submitted', 'dm_verified'].includes(st) && (canVerify || canApprove) && (
                            <button
                              onClick={() => { setRejectModal(b.id); setRejectReason(''); }}
                              disabled={isLoading}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
                            >
                              <XCircle className="w-3 h-3" /> Reject
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {rejectModal !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Reject Transport Bill</h3>
            <p className="text-sm text-gray-500 mb-4">Provide a reason for rejection.</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => { setRejectModal(null); setRejectReason(''); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleReject} disabled={!rejectReason.trim() || actionLoading === rejectModal}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50">
                {actionLoading === rejectModal && <Loader2 className="w-4 h-4 animate-spin inline mr-1" />}
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportBillsList;
